import type { InferInput, InferOutput, ObjectEntries, ObjectSchema } from "valibot";
import { safeParse } from "valibot";
import { useSearchParams } from "@solidjs/router";
import { createRenderEffect } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

export const useTypedSearchParams = <TEntries extends ObjectEntries>(
  schema: ObjectSchema<TEntries, undefined>,
): [InferOutput<typeof schema>, ReturnType<typeof useSearchParams>[1]] => {
  const [searchParams, setSearchParams] = useSearchParams<{
    [K in keyof InferInput<typeof schema>]: string;
  }>();

  const parse = (): InferOutput<typeof schema> => {
    const parseResult = safeParse(schema, { ...searchParams });
    if (!parseResult.success) {
      throw new Error("Schema should always have fallbacks");
    }
    return parseResult.output as InferOutput<typeof schema>;
  };

  // Wrap parse output into a store for reactivity with the same API as `useSearchParams`
  const [store, setStore] = createStore<InferOutput<typeof schema>>({} as InferOutput<typeof schema>);
  createRenderEffect(() => setStore(reconcile(parse())));

  return [store, setSearchParams];
};
